import { useTranslator } from 'tradera/lang/translate';
import { useAppDispatch, useAppSelector } from 'tradera/state/hooks';
import { ScrollableModal } from '../scrollable-modal/scrollable-modal';
import { closeReferralModal } from 'tradera/state/ui/reducer';
import GiftIcon from 'tradera/blueprint/theme/icons-colored/gift.svg?jsx';
import styles from './referral-modal.module.scss';
import { classNames } from 'tradera/utils/class-names';
import { useGetReferralCodeQuery } from './services/referral-modal-api';
import { Button } from '../atoms/button/button';
import { AccordionItem } from '../accordion/accordion';
import { LocalLoader } from '../loader/loader';
import { getEmailShareLink, getSmsShareLink } from 'tradera/utils/share-link';
import { useLocalizedPriceFormatter } from 'tradera/localization/use-localized-price-formatter';
import useIsLoggedIn from 'tradera/hooks/use-is-loggedin';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import ArrowRightIcon from 'tradera/blueprint/theme/icons2/arrow-right.svg?jsx';
import ShareIcon from 'tradera/blueprint/theme/icons2/share.svg?jsx';
import { HeaderWithCloseButton } from '../scrollable-modal/header-with-close-button';
import { useEffect, useState } from 'react';
import { isServer } from 'tradera/utils/nextjs';
import { showErrorToast } from 'tradera/services/toasts';
import { CurrencyRateType } from 'tradera/state/multi-currency/reducer';

export const ReferralModal = () => {
    const { t } = useTranslator();
    const dispatch = useAppDispatch();
    const localizedPriceFormatter = useLocalizedPriceFormatter(
        CurrencyRateType.Buyer,
    );
    const isMobile = useIsMobile();
    const isLoggedIn = useIsLoggedIn();
    const [codeCopied, setCodeCopied] = useState(false);
    const canShare = !isServer && navigator?.canShare?.({ text: 'text' });
    const gaCategory = 'Referral';
    const isOpen =
        useAppSelector((state) => state.ui.referralModal?.isOpen) && isLoggedIn;
    const { data, isFetching } = useGetReferralCodeQuery({}, { skip: !isOpen });
    const referral = data?.referralCode;
    const isError = data?.responseStatus?.errorCode;
    const referralCode = referral
        ? `${referral.substring(0, 4)}_${referral.substring(4)}`
        : '-';
    const formattedAmount = localizedPriceFormatter(data?.amount || 0);

    const onClose = () => {
        dispatch(closeReferralModal());
    };

    const copyCode = () => {
        navigator.clipboard.writeText(
            t('referralModal_share_copyBody', {
                code: referralCode,
                amount: formattedAmount,
            }),
        );
        setCodeCopied(true);
    };

    const nativeShare = () => {
        navigator.share({
            text: t('referralModal_share_nativeBody', {
                code: referralCode,
                amount: formattedAmount,
            }),
        });
    };

    useEffect(() => {
        if (isError) {
            dispatch(closeReferralModal());
            showErrorToast(
                'referralModal_error_generic',
                'referralModal_error_header',
            );
        }
    }, [isError, dispatch]);

    return (
        <ScrollableModal
            mobileBottomSheet
            show={isOpen}
            onClose={onClose}
            header={<HeaderWithCloseButton onClose={onClose} />}>
            <div className="d-flex flex-column">
                <GiftIcon
                    className={classNames(
                        'icon icon-xxl mb-4 align-self-center',
                        styles.iconHero,
                    )}
                />
                <h3 className="align-self-center mb-2 text-styled">
                    {!isFetching &&
                        t('referralModal_title', { amount: formattedAmount })}
                </h3>
                <div className="mb-3">
                    {!isFetching &&
                        t('referralModal_description', {
                            amount: formattedAmount,
                        })}
                </div>
                <div className="d-flex gap-x-2 mb-2 position-relative">
                    {isFetching && <LocalLoader />}
                    <div
                        className="h-100 border-bottom border-dark d-flex align-items-center text-inter-medium flex-grow-1 py-1"
                        style={{ fontVariantLigatures: 'none' }}>
                        {referralCode}
                    </div>
                    <Button size="xs" onClick={copyCode} className="px-5">
                        {codeCopied
                            ? t('referralModal_codeCopied')
                            : t('referralModal_copyCode')}
                    </Button>
                </div>
                {isMobile &&
                    (canShare ? (
                        <>
                            <Button
                                theme="unbutton"
                                iconLeft={
                                    <ShareIcon className="icon icon-md flex-shrink-0" />
                                }
                                iconRight={
                                    <ArrowRightIcon className="icon flex-shrink-0" />
                                }
                                onClick={nativeShare}
                                data-ga-category={gaCategory}
                                data-ga-action="Navigation"
                                data-ga-label="Sociala medier - Dela"
                                className="border-bottom py-2">
                                <span className="flex-grow-1 text-left">
                                    {t('referralModal_nativeShare')}
                                </span>
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                theme="unbutton"
                                iconRight={
                                    <ArrowRightIcon className="icon flex-shrink-0" />
                                }
                                href={getSmsShareLink({
                                    number: undefined,
                                    body: t('referralModal_share_smsBody', {
                                        code: referralCode,
                                        amount: formattedAmount,
                                    }),
                                })}
                                data-ga-category={gaCategory}
                                data-ga-action="Navigation"
                                data-ga-label="Sociala medier - SMS"
                                className="border-bottom py-2">
                                <span className="flex-grow-1 text-left">
                                    {t('referralModal_shareSms')}
                                </span>
                            </Button>
                            <Button
                                theme="unbutton"
                                iconRight={
                                    <ArrowRightIcon className="icon flex-shrink-0" />
                                }
                                href={getEmailShareLink({
                                    subject: undefined,
                                    body: t('referralModal_share_emailBody', {
                                        code: referralCode,
                                        amount: formattedAmount,
                                    }),
                                })}
                                data-ga-category={gaCategory}
                                data-ga-action="Navigation"
                                data-ga-label="Sociala medier - Mail"
                                className="border-bottom py-2">
                                <span className="flex-grow-1 text-left">
                                    {t('referralModal_shareEmail')}
                                </span>
                            </Button>
                        </>
                    ))}
                <AccordionItem
                    title={
                        <span className="text-inter-medium">
                            {t('referralModal_termsTitle')}
                        </span>
                    }
                    className="border-0">
                    {t('referralModal_termsDescription')}
                </AccordionItem>
            </div>
        </ScrollableModal>
    );
};
